import React from 'react';
import { styled } from '@mui/material/styles';

const StyledImg = styled('img')({});

/**
 * Image — an <img> that ALWAYS renders intrinsic width/height HTML attributes.
 *
 * Why this exists: <Box component="img" sx={{ width, height }} /> compiles the
 * dimensions into a runtime-injected emotion class and emits NO width/height
 * attributes. That (a) fails Lighthouse's `unsized-images` audit and (b) means
 * the browser can't reserve layout space until the JS-injected CSS applies, so
 * the element shifts on the prerender → client-render path. Real width/height
 * attributes let the browser reserve space from the aspect ratio before any CSS
 * or JS runs — the canonical CLS fix.
 *
 * Built on styled('img'), so unlike Box it forwards `width`/`height` to the DOM
 * as attributes while still accepting the MUI `sx` prop for styling. Pass the
 * INTRINSIC pixel size as `width`/`height` (this is what fixes the aspect
 * ratio); control the RENDERED size with `sx`/`style` (e.g.
 * `sx={{ width: { xs: '100%', md: 400 } }}`) — the attributes still reserve the
 * correct space.
 *
 * Defaults to lazy loading; pass `loading="eager"` (and `fetchPriority="high"`)
 * for above-the-fold images.
 */
const Image = React.forwardRef(function Image(
  { alt = '', loading = 'lazy', decoding = 'async', ...rest },
  ref
) {
  return (
    <StyledImg
      ref={ref}
      alt={alt}
      loading={loading}
      decoding={decoding}
      {...rest}
    />
  );
});

export default Image;
